import {
  getFontCss,
  HoverableLink,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import TextDropdown from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import {
  FormFieldsValidation,
  useFlyoutDispatch,
  useFormFields,
  useScrollToScrollGroupRefs,
} from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { getIssuers } from 'src/redux/actions/data/issuerIntegrators.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCountryListModelsFromState,
  selectCountryOptions,
} from 'src/redux/selectors/data/countrys.selectors'
import { selectIssuerIntegratorListModelsFromState } from 'src/redux/selectors/data/issuerIntegrators.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type AccountBillingInformationFlatFields = {
  country: string
}

export type AccountBillingInformationCountryFields = {
  address1: string
  address2?: string
  city: string
  province: string
  postal: string
}

interface AccountBillingInformationBoxProps {
  className?: string
  hasEditPermissions: boolean
  creditCard: CreditCardListModel
  puttingCreditCard: boolean
  puttingCreditCardBilling: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  onSuccessfulSubmit: (
    flatFields: AccountBillingInformationFlatFields,
    countryFields: AccountBillingInformationCountryFields,
  ) => void
  inFocus: boolean
}

const AccountBillingInformationBox: React.FC<
  AccountBillingInformationBoxProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const countryListModels = useSelector(selectCountryListModelsFromState)
  const countryOptions = useSelector(selectCountryOptions)
  const issuersMap = useSelector(selectIssuerIntegratorListModelsFromState)

  const dispatch = useFlyoutDispatch()

  useEffect((): void => {
    dispatch(getIssuers())
  }, [])

  const initialFlatFields: AccountBillingInformationFlatFields = {
    country: props.creditCard.address.country,
  }

  const selectedIssuer =
    props.creditCard.issuerId !== undefined
      ? issuersMap[props.creditCard.issuerId]
      : undefined

  const filteredCountryOptions =
    selectedIssuer?.restrictions?.country !== undefined
      ? countryOptions.filter((opt) =>
          selectedIssuer.restrictions?.country.includes(opt.value),
        )
      : countryOptions

  const flatFieldsValidation =
    (): FormFieldsValidation<AccountBillingInformationFlatFields> => ({
      country: [stringIsEmpty()],
    })
  const {
    fields: flatFields,
    setFields: setFlatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
    topmostScrollGroup: flatFieldsTopmostScrollGroup,
  } = useFormFields<AccountBillingInformationFlatFields>(
    initialFlatFields,
    flatFieldsValidation,
    props.errorDetailMap,
  )

  const countrySchema =
    countryListModels[flatFields.country] !== undefined
      ? countryListModels[flatFields.country].fields
      : undefined

  const initialCountryFields: AccountBillingInformationCountryFields = {
    address1: props.creditCard.address.address1,
    address2: props.creditCard.address.address2,
    city: props.creditCard.address.city,
    province: props.creditCard.address.province,
    postal: props.creditCard.address.postal,
  }
  const countryFieldsValidation =
    (): FormFieldsValidation<AccountBillingInformationCountryFields> =>
      countrySchema !== undefined
        ? countrySchema.reduce((acc, curr) => {
            if (!curr.optional) {
              return { ...acc, [curr.name]: [stringIsEmpty()] }
            } else {
              return acc
            }
          }, {})
        : {}
  const {
    fields: countryFields,
    setFields: setFlatCountryFields,
    fieldsErrors: countryFieldsErrors,
    onFieldChange: onCountryFieldChange,
    setErrorsAndReturnDidPassValidation: countryFieldsSetErrors,
    topmostScrollGroup: countryFieldsTopmostScrollGroup,
  } = useFormFields<AccountBillingInformationCountryFields>(
    initialCountryFields,
    countryFieldsValidation,
    props.errorDetailMap,
    {
      [`updateAccountRequest.postal`]: 'postal',
    },
  )

  const scrollRef0 = useRef<HTMLFormElement>(null)
  useScrollToScrollGroupRefs(
    [scrollRef0],
    [flatFieldsTopmostScrollGroup, countryFieldsTopmostScrollGroup],
  )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()
    const countryFieldsPassedValidation = countryFieldsSetErrors()
    if (flatFieldsPassedValidation && countryFieldsPassedValidation) {
      props.onSuccessfulSubmit(flatFields, countryFields)
    }
  }

  const isDirty = useMemo(
    () =>
      flatFields.country !== props.creditCard.address.country ||
      countryFields.address1 !== props.creditCard.address.address1 ||
      countryFields.address2 !== props.creditCard.address.address2 ||
      countryFields.city !== props.creditCard.address.city ||
      countryFields.postal !== props.creditCard.address.postal ||
      countryFields.province !== props.creditCard.address.province,
    [flatFields, countryFields, props.creditCard],
  )

  const [isEditing, setIsEditing] = useState<boolean>(false)

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          {translate('Billing Information')}
        </span>
        {props.hasEditPermissions &&
          !isEditing &&
          !props.creditCard.isClosed && (
            <HoverableLink
              css={css`
                display: flex;
                align-items: center;
              `}
              onClick={(): void => setIsEditing(true)}
              ariaLabelSource={`Update`}
            >
              {translate('Update')}
              <SvgUpdate
                css={css`
                  transform: scale(calc(7 / 12));
                `}
              />
            </HoverableLink>
          )}
      </div>
      {!countrySchema ? (
        <LoadingBoxBottom />
      ) : (
        <form
          ref={scrollRef0}
          onSubmit={onSubmit}
          css={css`
            display: flex;
            flex-direction: column;
            margin-top: 40px;
          `}
        >
          <FieldLabel>{translate('Country')}</FieldLabel>
          {isEditing ? (
            <TextDropdown
              data-testid={'accountBillingCountryDropdown'}
              disabled={!(filteredCountryOptions.length > 1)}
              errors={flatFieldsErrors['country']}
              placeholder={translateSingleString(
                locale,
                'Select ${fieldLabel}',
                {
                  fieldLabel: translateSingleString(locale, 'Country'),
                },
              )}
              isMulti={false}
              showUpdates
              baseValues={new Set([props.creditCard.address.country])}
              selectedValues={new Set([flatFields['country']])}
              chooseOption={(val: string): void => {
                onFlatFieldChange('country', val)
                if (val === props.creditCard.address.country) {
                  onCountryFieldChange(
                    'province',
                    props.creditCard.address.province,
                  )
                } else {
                  onCountryFieldChange('province', undefined)
                }
              }}
              optionGroups={[
                {
                  options: filteredCountryOptions,
                },
              ]}
            />
          ) : (
            <span>{flatFields['country']}</span>
          )}
          {countrySchema.map((field, i) => (
            <span
              key={`${flatFields.country}-${field.name}-${i}`}
              css={css`
                margin-top: 30px;
                display: flex;
                flex-direction: column;
              `}
            >
              <FieldLabel>{field.label}</FieldLabel>
              {isEditing ? (
                field.options !== undefined ? (
                  <TextDropdown
                    errors={countryFieldsErrors[field.name]}
                    placeholder={translateSingleString(
                      locale,
                      'Select ${fieldLabel}',
                      { fieldLabel: field.label },
                    )}
                    isMulti={false}
                    showUpdates
                    baseValues={
                      props.creditCard.address[field.name] !== undefined
                        ? new Set([
                            props.creditCard.address[field.name] as string,
                          ])
                        : new Set([])
                    }
                    selectedValues={
                      countryFields[field.name] !== undefined
                        ? new Set([countryFields[field.name] as string])
                        : new Set([])
                    }
                    chooseOption={(val: string): void => {
                      onCountryFieldChange(field.name, val)
                    }}
                    optionGroups={[
                      {
                        options: field.options.map((opt) => ({
                          value: opt.code,
                          details: {
                            text: opt.label,
                          },
                        })),
                      },
                    ]}
                  />
                ) : (
                  <TextInputField
                    errors={countryFieldsErrors[field.name]}
                    design={TextInputFieldDesigns.New}
                    value={countryFields[field.name]}
                    showUpdates
                    baseValue={props.creditCard.address[field.name]}
                    onChange={(val: string): void => {
                      onCountryFieldChange(field.name, val)
                    }}
                    placeholder={translateSingleString(
                      locale,
                      'Enter ${fieldLabel}',
                      { fieldLabel: field.label },
                    )}
                  />
                )
              ) : (
                <span>
                  {countryFields[field.name]
                    ? countryFields[field.name]
                    : '- -'}
                </span>
              )}
            </span>
          ))}
          {isEditing && (
            <div
              css={css`
                margin-top: 40px;
                display: flex;
                flex-direction: row;
                gap: 20px;
                align-items: center;
              `}
            >
              <Button
                type={ButtonTypes.submit}
                design={ButtonDesigns.designCta}
                text={translate('Save Changes')}
                loading={props.puttingCreditCardBilling}
                disabled={!isDirty || props.puttingCreditCard}
              />
              <HoverableLink
                onClick={(): void => {
                  setFlatFields(initialFlatFields)
                  setFlatCountryFields(initialCountryFields)
                  setIsEditing(false)
                }}
                ariaLabelSource={'Cancel'}
              >
                {translate('Cancel')}
              </HoverableLink>
            </div>
          )}
        </form>
      )}
      {props.topLevelError !== undefined && props.inFocus && (
        <TopLevelError
          css={css`
            align-self: center;
            margin-top: 20px;
          `}
          error={props.topLevelError}
        />
      )}
    </Box>
  )
}

export default AccountBillingInformationBox
